import {
    calculateVolume,
    calculateWeight,
} from "@/components/TransportOrder/CSVExport/csvExportHelpers"
import {
    isCargoFreight,
    ShipmentStatus,
    type Article,
    type HandlingUnit,
    type Packable,
    type Shipment,
} from "@/model/transportorder/transportOrder"
import { getClientTimezone, getTimezoneOfCountry } from "@/timezones/timezones"
import {
    formatInboundCsvDate,
    formatInboundCsvDateAndTime,
    formatInboundCsvTime,
} from "./inboundCsvFormatting"
import type { InboundCsvHeader } from "./inboundCsvSerializer"

export type InboundEmptiesCsvExportRow = {
    transportOrderNumber?: string
    externalOrderCreatedDate?: string
    orderLastModifiedDateTime?: string
    shipmentStatus?: string
    pickupDate?: string
    pickupFrom?: string
    pickupTo?: string
    deliveryDate?: string
    shipFromName?: string
    shipFromAddressCountry?: string
    shipFromAddressCity?: string
    shipFromAddressPostalCode?: string
    shipFromAddressStreet?: string
    shipFromPlaceOfLoading?: string
    shipFromDuns?: string
    shipFromSupplierCode?: string
    shipFromPlantCode?: string
    shipFromAccessCode?: string
    deliveringFreightForwarderName?: string
    deliveringFreightForwarderAddressCountry?: string
    deliveringFreightForwarderAddressPostalCode?: string
    deliveringFreightForwarderAddressCity?: string
    deliveringFreightForwarderAddressStreet?: string
    deliveringFreightForwarderDuns?: string
    deliveringFreightForwarderSupplierCode?: string
    calculatedArrivalAtCrossDock?: string
    shipToName?: string
    shipToAddressStreet?: string
    shipToAddressPostalCode?: string
    shipToAddressCity?: string
    shipToAddressCountry?: string
    shipToDuns?: string
    shipToSupplierCode?: string
    shipToPlantCode?: string
    shipToPlaceOfDischarge?: string
    handlingUnitArticleId?: string
    handlingUnitQuantity?: string
    handlingUnitVolume?: number
    handlingUnitWeight?: number
    handlingUnitEmptiesPackagingDetails?: string
}

/**
 * Property order is important here, it will define the order in the csv.
 */
const EMPTIES_HEADER_LABELS: Record<keyof InboundEmptiesCsvExportRow, string> = {
    transportOrderNumber: "Transport order number",
    externalOrderCreatedDate: "Order date",
    orderLastModifiedDateTime: "Last status time",
    shipmentStatus: "Last status",
    pickupDate: "Requested pickup date at ship-from",
    pickupFrom: "From",
    pickupTo: "To",
    deliveryDate: "Calculated delivery date at ship-to",
    shipFromName: "Ship-from name",
    shipFromAddressCountry: "Ship-from country",
    shipFromAddressPostalCode: "Ship-from zip code",
    shipFromAddressCity: "Ship-from city",
    shipFromAddressStreet: "Ship-from street",
    shipFromDuns: "Ship-from DUNS",
    shipFromSupplierCode: "Ship-from supplier number",
    shipFromPlantCode: "Ship-from plant code",
    shipFromPlaceOfLoading: "Ship-from pickup location",
    shipFromAccessCode: "Ship-from access code",
    deliveringFreightForwarderName: "Delivering freight forwarder name",
    deliveringFreightForwarderAddressCountry: "Delivering freight forwarder country",
    deliveringFreightForwarderAddressPostalCode: "Delivering freight forwarder zip code",
    deliveringFreightForwarderAddressCity: "Delivering freight forwarder city",
    deliveringFreightForwarderAddressStreet: "Delivering freight forwarder street",
    deliveringFreightForwarderDuns: "Delivering freight forwarder DUNS",
    deliveringFreightForwarderSupplierCode: "Delivering freight forwarder supplier number",
    calculatedArrivalAtCrossDock: "Calculated arrival at cross-dock",
    shipToName: "Ship-to name",
    shipToAddressCountry: "Ship-to country",
    shipToAddressPostalCode: "Ship-to zip code",
    shipToAddressCity: "Ship-to city",
    shipToAddressStreet: "Ship-to street",
    shipToDuns: "Ship-to DUNS",
    shipToSupplierCode: "Ship-to supplier number",
    shipToPlantCode: "Ship-to plant code",
    shipToPlaceOfDischarge: "Ship-to place of discharge",
    handlingUnitArticleId: "Article number",
    handlingUnitQuantity: "Quantity",
    handlingUnitVolume: "Volume [m3]",
    handlingUnitWeight: "Weight [kg]",
    handlingUnitEmptiesPackagingDetails: "Empties packaging details",
}

export const getInboundEmptiesCsvHeaders = (): InboundCsvHeader<InboundEmptiesCsvExportRow>[] =>
    (Object.entries(EMPTIES_HEADER_LABELS) as [keyof InboundEmptiesCsvExportRow, string][])
        .map(([key, label]) => ({ key, label }))

const getShipmentStatus = (shipment: Shipment): string => {
    if (shipment.status === ShipmentStatus.CANCELLED) {
        return "Pickup cancelled"
    }
    if (shipment.isConfirmed) {
        return "Pickup confirmed"
    }
    return "Pickup requested"
}

const isArticle = (packable: Packable): packable is Article =>
    packable.type === "ARTICLE"

const formatEmptiesPackagingDetails = (handlingUnit: HandlingUnit | undefined): string | undefined => {
    const details = handlingUnit?.content
        .filter(isArticle)
        .map(article => [
            article.quantity === undefined ? undefined : `${article.quantity}×`,
            article.article_id,
        ].filter(Boolean).join(" "))
        .filter(Boolean)
        .join(", ")

    return details || undefined
}

const mapHandlingUnit = (
    handlingUnit: HandlingUnit | undefined,
    shipment: Shipment,
): InboundEmptiesCsvExportRow => {
    const { currentTransportLeg, startOfTransportChain, endOfTransportChain, freightForwarder } = shipment

    const requestedPickupAtShipFrom = startOfTransportChain.pickupRequestedAt
    const estimatedDeliveryAtShipTo = endOfTransportChain.deliveryEstimatedAt
    const shipFrom = startOfTransportChain.consignor
    const shipTo = endOfTransportChain.consignee


    return {
        transportOrderNumber: shipment.external_identification,
        externalOrderCreatedDate: formatInboundCsvDate(shipment.createdDate, getClientTimezone()),
        orderLastModifiedDateTime: formatInboundCsvDateAndTime(
            shipment.last_modified_date,
            getClientTimezone(),
        ),
        shipmentStatus: getShipmentStatus(shipment),
        pickupDate: formatInboundCsvDate(
            requestedPickupAtShipFrom?.time_from,
            getTimezoneOfCountry(shipFrom.address?.country),
        ),
        pickupFrom: formatInboundCsvTime(
            requestedPickupAtShipFrom?.time_from,
            getTimezoneOfCountry(shipFrom.address?.country),
        ),
        pickupTo: formatInboundCsvTime(
            requestedPickupAtShipFrom?.time_to,
            getTimezoneOfCountry(shipFrom.address?.country),
        ),
        deliveryDate: formatInboundCsvDate(
            estimatedDeliveryAtShipTo?.time_from,
            getTimezoneOfCountry(shipTo.address?.country),
        ),
        shipFromName: shipFrom.name,
        shipFromAddressCountry: shipFrom.address?.country,
        shipFromAddressPostalCode: shipFrom.address?.postal_code,
        shipFromAddressCity: shipFrom.address?.city,
        shipFromAddressStreet: shipFrom.address?.street,
        shipFromPlaceOfLoading: shipment.startOfTransportChain.placeOfLoading,
        shipFromDuns: shipFrom.externalIdentifiers.duns,
        shipFromSupplierCode: shipFrom.externalIdentifiers.supplierCode,
        shipFromPlantCode: shipFrom.externalIdentifiers.plantCode,
        shipFromAccessCode: startOfTransportChain.accessCode,
        deliveringFreightForwarderName: freightForwarder.name,
        deliveringFreightForwarderAddressCountry: freightForwarder.address?.country,
        deliveringFreightForwarderAddressPostalCode: freightForwarder.address?.postal_code,
        deliveringFreightForwarderAddressCity: freightForwarder.address?.city,
        deliveringFreightForwarderAddressStreet: freightForwarder.address?.street,
        deliveringFreightForwarderDuns: freightForwarder.duns,
        deliveringFreightForwarderSupplierCode: freightForwarder.supplierCode,
        calculatedArrivalAtCrossDock: formatInboundCsvDate(
            currentTransportLeg.arrivalEstimatedAt?.time_to,
            getTimezoneOfCountry(currentTransportLeg.placeOfArrival.address?.country),
        ),
        shipToName: shipTo.name,
        shipToAddressStreet: shipTo.address?.street,
        shipToAddressPostalCode: shipTo.address?.postal_code,
        shipToAddressCity: shipTo.address?.city,
        shipToAddressCountry: shipTo.address?.country,
        shipToDuns: shipTo.externalIdentifiers.duns,
        shipToSupplierCode: shipTo.externalIdentifiers.supplierCode,
        shipToPlantCode: shipTo.externalIdentifiers.plantCode,
        shipToPlaceOfDischarge: endOfTransportChain.placeOfDischarge,
        handlingUnitArticleId: handlingUnit?.packaging_material_id,
        handlingUnitQuantity: handlingUnit?.quantity?.toString(),
        handlingUnitVolume: handlingUnit && calculateVolume(handlingUnit),
        handlingUnitWeight: handlingUnit && calculateWeight(handlingUnit),
        handlingUnitEmptiesPackagingDetails: formatEmptiesPackagingDetails(handlingUnit),
    }
}

export const mapInboundEmptiesShipmentsToCsvRows = (
    shipments: Shipment[],
): InboundEmptiesCsvExportRow[] =>
    shipments.flatMap(shipment => {
        if (!isCargoFreight(shipment.freight)) {
            return [mapHandlingUnit(undefined, shipment)]
        }
        return shipment.freight.handling_units.map(handlingUnit =>
            mapHandlingUnit(handlingUnit, shipment),
        )
    })
